// ---------------------------------------------------------------------------
// V32 — INVOICING AND MARGIN, per event.
//
// ★ THE ONE PLACE THE TWO SIDES LEGITIMATELY MEET. `requirement.charge_rate_minor`
// and `event.budget_minor` are the two fields the client portal is built never to
// show, and this screen shows both. That is not a relaxation: it is why the
// margin is `finance.read` and never external, refused in the database rather
// than here, so a scheduler who plans the headcount does not see the price and a
// client who reads the roster does not see the cost.
//
// THE UNPRICEABLE ROW IS THE ONE TO LOOK AT. A requirement whose pinned pay rule
// set states no unconditional base rate cannot have its cost projected, and it is
// DROPPED FROM THE TOTAL rather than counted as free. Scored zero it would read
// as a hundred per cent margin, which is the most attractive wrong number this
// product could show a person setting a price — so the row stays on the screen,
// carries the database's own sentence saying why, and the summary says how many
// it left out.
//
// NO ARITHMETIC IN THIS FILE. Every figure arrives computed; `money` puts the
// decimal point in and `percent` turns basis points into a percentage, both from
// `system/format.ts`, which is the one module allowed to divide.
// ---------------------------------------------------------------------------

import { Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import {
  invoicing as invoicingApi,
  type InvoiceDocument,
  type InvoiceRow,
  type MarginRequirement,
  type MarginSummary,
  type PayrollExport,
  type PayrollFormat,
  type VatTreatment,
} from "../api/invoicing";
import { AddButton } from "../design/AddLink";
import { Tabs } from "../design/Tabs";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { download } from "../system/download";
import { csv, money, percent, whenIn } from "../system/format";
import { duration } from "../system/pay";

type MarginReply = {
  requirements?: MarginRequirement[];
  summary?: MarginSummary | null;
  error?: { message: string };
};
type InvoicesReply = { invoices?: InvoiceRow[]; error?: { message: string } };
type FormatsReply = { formats?: PayrollFormat[]; error?: { message: string } };

export function Invoicing() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const params = useParams();
  const eventId = params["id"] ?? "";

  // THE ORGANISATION'S CLOCK, for `HANDOFF.md` §6's reason: an invoice issued at
  // 23:00 in one country and read at 08:00 in another is the ordinary case for an
  // agency working across a border, and a time rendered in the reader's browser
  // zone is a different time from the one in the audit row it came from.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;

  const [requirements, setRequirements] = useState<MarginRequirement[] | null>(null);
  const [summary, setSummary] = useState<MarginSummary | null>(null);
  const [marginFailed, setMarginFailed] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [invoicesFailed, setInvoicesFailed] = useState<string | null>(null);
  const [formats, setFormats] = useState<PayrollFormat[] | null>(null);
  const [formatsFailed, setFormatsFailed] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await send<MarginReply>(invoicingApi.margin(apiBase, eventId));
      if (res.status === 200 && Array.isArray(res.body?.requirements)) {
        setRequirements(res.body.requirements);
        setSummary(res.body.summary ?? null);
        setMarginFailed(null);
      } else {
        setRequirements(null);
        setSummary(null);
        setMarginFailed(refusal(res.body?.error) ?? t("The margin could not be read."));
      }
    })();
  }, [apiBase, eventId]);

  const loadInvoices = useCallback(async () => {
    const res = await send<InvoicesReply>(invoicingApi.invoices(apiBase, eventId));
    if (res.status === 200 && Array.isArray(res.body?.invoices)) {
      setInvoices(res.body.invoices);
      setInvoicesFailed(null);
    } else {
      setInvoices(null);
      setInvoicesFailed(refusal(res.body?.error) ?? t("The invoices could not be read."));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  // SWEEP S10. THE SCREEN COULD READ AN INVOICE AND NEVER RAISE ONE. `issue`, `invoice`
  // and `takeExport` had no caller; the finance persona could see the margin and not bill it.
  const mayIssue = me ? may(me, "invoice.issue") : false;
  const mayExport = me ? may(me, "payroll.export") : false;
  const [issuing, setIssuing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [issueMessage, setIssueMessage] = useState<string | null>(null);
  const [issueNote, setIssueNote] = useState<string | null>(null);
  const [opened, setOpened] = useState<InvoiceDocument | null>(null);
  // 0278. The VAT is stated at every issue, from the database's own list.
  const [treatments, setTreatments] = useState<VatTreatment[]>([]);
  const [vatTreatment, setVatTreatment] = useState("");
  const [vatRate, setVatRate] = useState("");
  const chosen = treatments.find((x) => x.id === vatTreatment) ?? null;
  const vatReady = chosen !== null && (!chosen.has_rate || Number(vatRate) > 0);

  useEffect(() => {
    if (!mayIssue) return;
    void (async () => {
      const res = await send<{ vat_treatments?: VatTreatment[] }>(invoicingApi.vatTreatments(apiBase));
      if (res.status === 200) setTreatments(res.body.vat_treatments ?? []);
    })();
  }, [apiBase, mayIssue]);

  async function issue() {
    setIssuing(true);
    setIssueMessage(null);
    setIssueNote(null);
    try {
      const res = await send<{
        invoice?: { expenses_left_out?: { currency: string }[] } | null;
        error?: { message?: string };
      }>(
        invoicingApi.issue(apiBase, eventId, {
          vat_treatment: vatTreatment,
          ...(chosen?.has_rate ? { vat_rate: Number(vatRate) } : {}),
        }),
      );
      if (res.status !== 201) {
        setIssueMessage(refusal(res.body?.error) ?? t("The invoice could not be issued."));
        return;
      }
      // 0209 decision 3: a recharged claim in another currency is not billed, and says so.
      const leftOut = res.body?.invoice?.expenses_left_out ?? [];
      if (leftOut.length > 0) {
        setIssueNote(
          `${leftOut.length} recharged expense(s) in ${[...new Set(leftOut.map((x) => x.currency))].join(", ")} ` +
            "were left off: an invoice is in one currency. They stay unbilled.",
        );
      }
      setAdding(false);
      await loadInvoices();
    } catch {
      setIssueMessage(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setIssuing(false);
    }
  }

  async function open(id: string) {
    if (opened?.id === id) return setOpened(null);
    const res = await send<{ invoice?: InvoiceDocument; error?: { message?: string } }>(invoicingApi.invoice(apiBase, id));
    if (res.status === 200 && res.body.invoice) setOpened(res.body.invoice);
    else setIssueMessage(refusal(res.body?.error) ?? t("That invoice could not be opened."));
  }

  useEffect(() => {
    void (async () => {
      const res = await send<FormatsReply>(invoicingApi.formats(apiBase));
      if (res.status === 200 && Array.isArray(res.body?.formats)) {
        setFormats(res.body.formats);
        setFormatsFailed(null);
      } else {
        setFormats(null);
        setFormatsFailed(refusal(res.body?.error) ?? t("The payroll formats could not be read."));
      }
    })();
  }, [apiBase]);

  return (
    <div className="stack">
      <h1>{t("Invoicing and margin")}</h1>
      <p className="muted">
        {t("What this event will bill, what it will cost, and the difference. Nobody outside this agency can open this page.")}
      </p>

      <Tabs
        label={t("Invoicing and margin")}
        tabs={[
          {
            id: "inv-margin",
            label: t("Margin"),
            content: (
              <div className="stack">
                {marginFailed !== null ? (
                  <section className="panel">
                    <StatusChip tone="failed" label={t("The margin could not be read")} />
                    <p className="muted">{marginFailed}</p>
                  </section>
                ) : null}

                {summary !== null ? (
                  <section className="panel">
                    <h2>{t("Projected")}</h2>
                    {/* `facts`, WHICH IS THE CLASS THIS PRODUCT HAS. The first draft wrote
                        `detail-grid`, which has no rule anywhere in web/src/design — the
                        exact mistake V30's screen shipped, where two invented class names
                        left every element under them with no styling at all. Checked
                        against the stylesheet rather than remembered. */}
                    <dl className="facts">
                      <dt>{t("Charged")}</dt>
                      <dd className="numeric">
                        {money(summary.projected_charge_minor, summary.currency)}
                      </dd>
                      <dt>{t("Cost")}</dt>
                      <dd className="numeric">
                        {money(summary.projected_pay_minor, summary.currency)}
                      </dd>
                      <dt>{t("Margin")}</dt>
                      <dd className="numeric">
                        {money(summary.projected_margin_minor, summary.currency)}{" "}
                        <span className="muted">{percent(summary.margin_basis_points)}</span>
                      </dd>
                      <dt>{t("Budget")}</dt>
                      <dd className="numeric">
                        {money(summary.budget_minor, summary.budget_currency)}{" "}
                        {summary.over_budget === null ? null : (
                          <StatusChip
                            tone={summary.over_budget ? "attention" : "live"}
                            label={summary.over_budget ? t("Over budget") : t("Inside budget")}
                          />
                        )}
                      </dd>
                    </dl>
                    {/* ★ THE DENOMINATOR, STATED. A total over three of four posts is a
                        different number from a total over four, and a reader who is not
                        told cannot know which one they are looking at. */}
                    <p className="muted">
                      {t("{priceable} of {total} post(s) could be priced.", {
                        priceable: summary.requirements_priceable,
                        total: summary.requirements_total,
                      })}{" "}
                      {summary.requirements_priceable < summary.requirements_total
                        ? t("The rest are left out of every figure above rather than counted as costing nothing — see the table.")
                        : t("Every post on this event is in the figures above.")}
                    </p>
                  </section>
                ) : null}

                {requirements !== null ? (
                  <section className="panel">
                    <h2>{t("Post by post")}</h2>
                    {requirements.length === 0 ? (
                      <p className="muted">
                        {t("This event has no posts yet, so there is nothing to price. Add requirements first.")}
                      </p>
                    ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("Post")}</th>
                            <th scope="col">{t("Heads")}</th>
                            <th scope="col">{t("Payable minutes")}</th>
                            <th scope="col">{t("Charge")}</th>
                            <th scope="col">{t("Cost")}</th>
                            <th scope="col">{t("Margin")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requirements.map((r) => (
                            <tr key={r.requirement_id}>
                              <td>
                                {translateLabel(locale, r.role_name)}
                                {r.zone !== null ? (
                                  <>
                                    <br />
                                    <span className="muted">{r.zone}</span>
                                  </>
                                ) : null}
                                {r.is_priceable ? null : (
                                  <>
                                    <br />
                                    {/* NEVER COLOUR ALONE: a word as well as a tone, because
                                        this is read on a cracked phone, in the rain, at 06:00.
                                        The usual phrasing of that rule names a job role, and
                                        `strip_ts_comments` strips `//` lines and JSDoc `*`
                                        continuations but NOT the middle lines of a JSX block
                                        comment — so a role word here is a LIVE string to the
                                        taxonomy guard and failed thirty-seven children. Recorded
                                        in the morning file; the stripper is the thing to fix. */}
                                    <StatusChip tone="attention" label={t("Not priced")} />
                                    <br />
                                    <span className="muted">{r.not_priceable_reason}</span>
                                  </>
                                )}
                              </td>
                              <td className="numeric">{r.headcount}</td>
                              <td className="numeric">{duration(r.minutes_total)}</td>
                              <td className="numeric">{money(r.projected_charge_minor, r.currency)}</td>
                              <td className="numeric">{money(r.projected_pay_minor, r.currency)}</td>
                              <td className="numeric">{money(r.projected_margin_minor, r.currency)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>
                ) : null}
              </div>
            ),
          },
          {
            id: "inv-invoices",
            label: t("Invoices"),
            content: (
              <div className="stack">
                <section className="panel">
                  <div className="panel-head">
                    <h2>{t("Invoices")}</h2>
                    {mayIssue ? <AddButton label={t("Issue an invoice")} expanded={adding} onClick={() => setAdding(!adding)} /> : null}
                  </div>
                  {mayIssue && adding ? (
                    <div className="form-grid">
                      <div className="field">
                        <label htmlFor="inv-vat-treatment">{t("VAT treatment")}</label>
                        <select id="inv-vat-treatment" value={vatTreatment} onChange={(e) => setVatTreatment(e.target.value)}>
                          <option value="">{t("Choose…")}</option>
                          {treatments.map((x) => (
                            <option key={x.id} value={x.id}>
                              {translateLabel(locale, x.label)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {chosen?.has_rate ? (
                        <div className="field">
                          <label htmlFor="inv-vat-rate">{t("VAT rate (%)")}</label>
                          <input
                            id="inv-vat-rate"
                            type="number"
                            inputMode="decimal"
                            min={0.001}
                            max={100}
                            step="any"
                            value={vatRate}
                            onChange={(e) => setVatRate(e.target.value)}
                          />
                        </div>
                      ) : null}
                      <div className="row">
                        <button type="button" className="btn" disabled={issuing || !vatReady} onClick={() => void issue()}>
                          {issuing ? t("Issuing…") : t("Issue an invoice")}
                        </button>
                        <button type="button" className="btn btn-quiet" onClick={() => setAdding(false)}>
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {issueMessage ? (
                    <div className="form-message" role="status" aria-live="polite">
                      <StatusChip tone="attention" label={t("Not issued")} />
                      <span>{issueMessage}</span>
                    </div>
                  ) : null}
                  {issueNote ? (
                    <div className="form-message" role="status" aria-live="polite">
                      <StatusChip tone="attention" label={t("Issued without some expenses")} />
                      <span>{issueNote}</span>
                    </div>
                  ) : null}
                  {invoicesFailed !== null ? (
                    <>
                      <StatusChip tone="failed" label={t("The invoices could not be read")} />
                      <p className="muted">{invoicesFailed}</p>
                    </>
                  ) : invoices !== null && invoices.length === 0 ? (
                    <p className="muted">
                      {t("Nothing has been invoiced for this event. An invoice is raised from the charge calculations once the hours are approved and priced.")}
                    </p>
                  ) : invoices !== null ? (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">{t("Reference")}</th>
                          <th scope="col">{t("Issued")}</th>
                          <th scope="col">{t("Lines")}</th>
                          <th scope="col">{t("Evidence")}</th>
                          <th scope="col">{t("Standing")}</th>
                          <th scope="col">&nbsp;</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((i) => (
                          <Fragment key={i.id}>
                          <tr>
                            <td>{i.reference}</td>
                            <td>{whenIn(i.issued_at, orgZone)}</td>
                            <td>{i.line_count}</td>
                            <td>
                              {/* THE PROOF PACK, ATTACHED OR HONESTLY ABSENT. A missing pack
                                  says so rather than being left blank, because a blank cell
                                  reads as "no evidence needed". */}
                              <StatusChip
                                tone={i.proof_pack_id !== null ? "live" : "neutral"}
                                label={i.proof_pack_id !== null ? t("Proof Pack attached") : t("No Proof Pack")}
                              />
                            </td>
                            <td>
                              {i.is_superseded ? (
                                <StatusChip tone="neutral" label={t("Superseded by a correction")} />
                              ) : i.supersedes_id !== null ? (
                                <StatusChip tone="live" label={t("Current — a correction")} />
                              ) : (
                                <StatusChip tone="live" label={t("Current")} />
                              )}
                            </td>
                            <td>
                              <button type="button" className="btn btn-quiet" onClick={() => void open(i.id)}>
                                {opened?.id === i.id ? t("Close") : t("Open")}
                              </button>
                            </td>
                          </tr>
                          {opened?.id === i.id ? (
                            <tr>
                              <td colSpan={6}>
                                <InvoiceDocumentView doc={opened} orgZone={orgZone} />
                              </td>
                            </tr>
                          ) : null}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </section>
              </div>
            ),
          },
          {
            id: "inv-payroll",
            label: t("Payroll formats"),
            content: (
              <div className="stack">
                <section className="panel">
                  <h2>{t("Payroll formats")}</h2>
                  <p className="muted">
                    {t("The national formats configured here, and — for each — the columns its published specification requires that this product does not hold. Read those before sending a file to a bureau: they are stated rather than left blank on purpose.")}
                  </p>
                  {formatsFailed !== null ? (
                    <>
                      <StatusChip tone="failed" label={t("The payroll formats could not be read")} />
                      <p className="muted">{formatsFailed}</p>
                    </>
                  ) : formats !== null && formats.length === 0 ? (
                    <p className="muted">
                      {t("No payroll format is configured, so no bureau file can be produced here yet.")}
                    </p>
                  ) : formats !== null ? (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">{t("Country")}</th>
                          <th scope="col">{t("Format")}</th>
                          <th scope="col">{t("Specification")}</th>
                          <th scope="col">{t("Columns")}</th>
                          <th scope="col">{t("What we cannot fill in")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formats.map((f) => (
                          <tr key={f.id}>
                            <td>{f.country_code}</td>
                            <td>{f.name}</td>
                            <td>
                              {f.spec_reference}
                              <br />
                              <span className="muted">{f.spec_version}</span>
                            </td>
                            <td>{f.column_count}</td>
                            <td>
                              {f.gaps.length === 0 ? (
                                <StatusChip tone="live" label={t("Every required column can be filled")} />
                              ) : (
                                <>
                                  <StatusChip
                                    tone="attention"
                                    label={t("{n} required column(s) not held", { n: f.gaps.length })}
                                  />
                                  <ul className="plain-list muted">
                                    {f.gaps.map((g) => (
                                      <li key={g.column_name}>
                                        <strong>{g.column_name}</strong> — {g.note}
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                  {mayExport && formats !== null && formats.some((f) => f.is_enabled) ? (
                    <PayrollExportForm formats={formats.filter((f) => f.is_enabled)} />
                  ) : null}
                </section>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

/** S10. One invoice as issued: its lines, its total, and a file the client can be sent. */
function InvoiceDocumentView({ doc, orgZone }: { doc: InvoiceDocument; orgZone: string | null }) {
  const { t, locale } = useT();
  // S66: an expense line has no minutes. The amount is the database's integer minor
  // units, unchanged — a float division here put a second engine in the file.
  // D88-13's open half: each line says its VAT treatment and rate, and the per-group VAT
  // and the gross follow the lines, as the document shows them. Minor units, unchanged.
  const label = new Map((doc.vat ?? []).map((g) => [g.vat_treatment, translateLabel(locale, g.label)]));
  const rows = [[t("Line"), t("Description"), t("Payable minutes"), t("Amount ({currency}, minor units)", { currency: doc.currency }), t("VAT treatment"), t("VAT rate (%)")]].concat(
    doc.lines.map((l) => [String(l.seq), l.label, l.payable_minutes ?? "", l.amount_minor,
      label.get(l.vat_treatment ?? "") ?? l.vat_treatment ?? "", l.vat_rate === undefined ? "" : String(Number(l.vat_rate))]),
    [["", t("Net total"), "", doc.total_minor, "", ""]],
    (doc.vat ?? []).map((g) => ["", t("VAT: {label}, {rate}%", { label: translateLabel(locale, g.label), rate: Number(g.vat_rate) }), "", g.vat_minor, "", ""]),
    doc.gross_minor !== undefined ? [["", t("Total with VAT"), "", doc.gross_minor, "", ""]] : [],
  );
  return (
    <div className="stack">
      <dl className="facts">
        <dt>{t("Reference")}</dt>
        <dd>{doc.reference}</dd>
        <dt>{t("Event")}</dt>
        <dd>{doc.event_name}</dd>
        <dt>{t("Issued")}</dt>
        <dd>{whenIn(doc.issued_at, orgZone)}</dd>
        <dt>{t("Net total")}</dt>
        <dd className="numeric">{money(doc.total_minor, doc.currency)}</dd>
        {(doc.vat ?? []).map((g) => (
          <Fragment key={`${g.vat_treatment}-${g.vat_rate}`}>
            <dt>{t("VAT: {label}, {rate}%", { label: translateLabel(locale, g.label), rate: Number(g.vat_rate) })}</dt>
            <dd className="numeric">{money(g.vat_minor, doc.currency)}</dd>
          </Fragment>
        ))}
        {doc.gross_minor !== undefined ? (
          <>
            <dt>{t("Total with VAT")}</dt>
            <dd className="numeric">{money(doc.gross_minor, doc.currency)}</dd>
          </>
        ) : null}
        {doc.correction_reason ? (
          <>
            <dt>{t("Correction")}</dt>
            <dd>{doc.correction_reason}</dd>
          </>
        ) : null}
      </dl>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t("Line")}</th>
            <th scope="col">{t("Description")}</th>
            <th scope="col">{t("Amount")}</th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.map((l) => (
            <tr key={l.seq}>
              <td>{l.seq}</td>
              <td>{l.label}</td>
              <td className="numeric">{money(l.amount_minor, doc.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row">
        <button
          type="button"
          className="btn btn-quiet"
          onClick={() => download(`${doc.reference}.csv`, csv(rows), "text/csv")}
        >
          {t("Download as CSV")}
        </button>
      </div>
    </div>
  );
}

/** S10. Take a bureau file for a period. The file is rendered and recorded by the server. */
function PayrollExportForm({ formats }: { formats: PayrollFormat[] }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [formatId, setFormatId] = useState(formats[0]?.id ?? "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [paidOn, setPaidOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: "live" | "failed"; text: string } | null>(null);

  async function take() {
    if (!from || !to || !paidOn) return setResult({ tone: "failed", text: t("Give the period and the pay date.") });
    setBusy(true);
    setResult(null);
    try {
      const res = await send<PayrollExport & { error?: { message?: string } }>(
        invoicingApi.takeExport(apiBase, { format_id: formatId, period_from: from, period_to: to, paid_on: paidOn }),
      );
      if (res.status !== 201) {
        setResult({ tone: "failed", text: refusal(res.body?.error) ?? t("The file could not be produced.") });
        return;
      }
      download(`${formatId}-${from}-${to}.csv`, res.body.payload, "text/csv");
      setResult({
        tone: "live",
        text: t("{rows} rows. Fingerprint {fingerprint}… — recorded, so the same file can be shown to have been sent.", {
          rows: res.body.row_count,
          fingerprint: res.body.payload_sha256.slice(0, 12),
        }),
      });
    } catch {
      setResult({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void take();
      }}
    >
      <h3>{t("Take a payroll file")}</h3>
      <div className="row">
        <div className="field">
          <label htmlFor="px-format">{t("Format")}</label>
          <select id="px-format" value={formatId} onChange={(e) => setFormatId(e.target.value)}>
            {formats.map((f) => (
              <option key={f.id} value={f.id}>
                {f.country_code} — {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="px-from">{t("From")}</label>
          <input id="px-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="px-to">{t("To")}</label>
          <input id="px-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="px-paid">{t("Paid on")}</label>
          <input id="px-paid" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </div>
      </div>
      {result ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={result.tone} label={result.tone === "live" ? t("Taken") : t("Not taken")} />
          <span>{result.text}</span>
        </div>
      ) : null}
      <button type="submit" className="btn" disabled={busy}>
        {busy ? t("Producing…") : t("Take the file")}
      </button>
    </form>
  );
}
